/**
 * Keep the screen on while the guest needs it — and only then.
 *
 * Built on the Screen Wake Lock API. A held lock is released by the browser
 * whenever the page is hidden (tab switch, screen off, app in background);
 * while the guest still WANTS it, it is requested again as soon as the page is
 * visible. release() — called by the screen that asked for it, at the latest
 * when it is left — ends it for good.
 *
 * Unsupported browsers (older iOS, Firefox) report `isSupported() === false`;
 * callers hide their switch rather than offering one that does nothing.
 */
export class WakeLock
{
    constructor()
    {
        this._sentinel = null;
        this._wanted = false;
        this._onVisible = () =>
        {
            if ( this._wanted && document.visibilityState === "visible" && !this._sentinel )
            {
                this._acquire();
            }
        };
    }

    static isSupported()
    {
        return typeof navigator !== "undefined" && !!navigator.wakeLock && typeof navigator.wakeLock.request === "function";
    }

    get active()
    {
        return this._wanted;
    }

    /** @returns {Promise<boolean>} whether the screen is now kept on */
    async request()
    {
        if ( !WakeLock.isSupported() )
        {
            return false;
        }
        if ( !this._wanted )
        {
            this._wanted = true;
            document.addEventListener( "visibilitychange", this._onVisible );
        }
        const ok = await this._acquire();
        if ( !ok )
        {
            this.release();
        }
        return ok;
    }

    release()
    {
        this._wanted = false;
        document.removeEventListener( "visibilitychange", this._onVisible );
        if ( this._sentinel )
        {
            this._sentinel.release().catch( () => {} );
            this._sentinel = null;
        }
    }

    async _acquire()
    {
        try
        {
            this._sentinel = await navigator.wakeLock.request( "screen" );
            // Released by the browser (page hidden, battery saver): forget the
            // sentinel; _onVisible asks again if the guest still wants it.
            this._sentinel.addEventListener( "release", () => { this._sentinel = null; } );
            return true;
        }
        catch ( e )
        {
            // NotAllowedError: hidden page, battery saver, or a policy.
            this._sentinel = null;
            return false;
        }
    }
}
